import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { ChevronLeftIcon, ChevronRightIcon } from '../../styled/Icon';

import {
  OverflowSlider as StyledOverflowSlider,
  Slider,
  NavLeft,
  NavRight,
  FadeLeft,
  FadeRight,
} from './wrappers';
import useOnce from '../../hooks/useOnce';

interface IProps {
  bleed?: boolean;
  centered?: boolean;
  children: React.ReactNode;
  drilldown?: boolean;
  hash?: string;
  onScrollOnce?: () => void;
  showFade?: boolean;
  stepSize?: number;
}

const OverflowSlider = ({
  bleed,
  centered,
  children,
  drilldown,
  hash,
  onScrollOnce,
  showFade = true,
  stepSize = 1,
}: IProps) => {
  const [showNavLeft, setShowNavLeft] = useState(false);
  const [showNavRight, setShowNavRight] = useState(false);
  const [sliderDragged, setSliderDragged] = useState(false);
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const notifyScroll = useOnce(onScrollOnce);

  useEffect(() => {
    checkNavState();

    sliderRef.current?.addEventListener('scroll', checkScroll);
    sliderRef.current?.addEventListener('touchmove', onSliderDragged, { passive: true });
    window.addEventListener('resize', checkResize);

    return () => {
      sliderRef.current?.removeEventListener('scroll', checkScroll);
      sliderRef.current?.removeEventListener('touchmove', onSliderDragged);
      window.removeEventListener('resize', checkResize);
    };
  }, [sliderRef, children]);

  useEffect(() => {
    sliderRef.current?.scrollTo({
      left: 0,
    });
  }, [hash]);

  let scrollDebounce = 0;
  let resizeDebounce = 0;

  const onSliderDragged = () => {
    setSliderDragged(true);
    notifyScroll();
  };

  const checkScroll = () => {
    clearTimeout(scrollDebounce);
    scrollDebounce = window.setTimeout(checkNavState, 16);
    notifyScroll();
  };

  const checkResize = () => {
    clearTimeout(resizeDebounce);
    resizeDebounce = window.setTimeout(checkNavState, 100);
  };

  const checkNavState = () => {
    setShowNavLeft(!!sliderRef.current?.scrollLeft);
    setShowNavRight(
      (sliderRef.current?.scrollLeft || 0) <
        (sliderRef.current?.scrollWidth || 0) - (sliderRef.current?.offsetWidth || 0),
    );
  };

  const onNavLeft = () => {
    sliderRef.current?.scrollTo({
      left: (sliderRef.current?.scrollLeft || 0) - sliderRef.current?.offsetWidth * stepSize,
      behavior: 'smooth',
    });
  };

  const onNavRight = () => {
    sliderRef.current?.scrollTo({
      left: (sliderRef.current?.scrollLeft || 0) + sliderRef.current?.offsetWidth * stepSize,
      behavior: 'smooth',
    });
  };

  return (
    <StyledOverflowSlider>
      <Slider ref={sliderRef} className={clsx(centered && 'centered', bleed && 'bleed')}>
        {children}
      </Slider>
      {showNavLeft && (
        <>
          {showFade && <FadeLeft />}
          <NavLeft
            onClick={onNavLeft}
            className={clsx(sliderDragged && 'fadeout', drilldown && 'drilldown')}>
            <ChevronLeftIcon strokeWidth="2" />
          </NavLeft>
        </>
      )}
      {showNavRight && (
        <>
          {showFade && <FadeRight />}
          <NavRight
            onClick={onNavRight}
            className={clsx(sliderDragged && 'fadeout', drilldown && 'drilldown')}>
            <ChevronRightIcon strokeWidth="2" />
          </NavRight>
        </>
      )}
    </StyledOverflowSlider>
  );
};

export default OverflowSlider;
